import { type Api } from "api-client";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Badge, Tooltip, TooltipContent, TooltipTrigger } from "ui";

import { SectionFormatIcon } from "src/workouts/SectionFormatIcon";

type Section = Api.ListSections.Output.Self["currentPage"][number];
type RootItem = Section["rootItems"][number];

function getFormatLabel(
  t: TFunction,
  format: string | null,
  groupType: string | null,
): string | null {
  if (groupType === "SUPER_SET") return t("sectionsPage.format.superset");

  switch (format) {
    case "REGULAR":
      return t("sectionsPage.format.regular");
    case "AMRAP":
      return t("sectionsPage.format.amrap");
    case "TIMED":
      return t("sectionsPage.format.timed");
    case "FREESTYLE":
      return t("sectionsPage.format.freestyle");
    default:
      return null;
  }
}

function getGroupTypeLabel(t: TFunction, groupType: string | null) {
  switch (groupType) {
    case "WARM_UP":
      return t("sectionsPage.properties.warmup");
    case "WORKOUT":
      return t("sectionsPage.properties.workout");
    case "COOLDOWN":
      return t("sectionsPage.properties.cooldown");
    case "RECOVERY":
      return t("sectionsPage.properties.recovery");
    default:
      return null;
  }
}

export function SectionDetails(props: { section: Section }) {
  const { t } = useTranslation();

  if (props.section.rootItems.length === 0) return null;

  function renderBadgeContent(item: RootItem) {
    if (item.format === "FREESTYLE" || item.groupType === "SUPER_SET") {
      return item.exerciseCount > 0 ? `x${item.exerciseCount}` : null;
    }

    return getGroupTypeLabel(t, item.groupType);
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {props.section.rootItems.map((item, index) => {
        const formatLabel = getFormatLabel(t, item.format, item.groupType);

        if (!formatLabel) return null;

        return (
          <Tooltip key={index}>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="gap-1">
                <SectionFormatIcon format={item.format} />
                {renderBadgeContent(item)}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {t("sectionsPage.formatTooltip", { format: formatLabel })}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
